import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
interface HeroSectionProps {
  onExploreClick: () => void;
}
export const HeroSection = ({
  onExploreClick
}: HeroSectionProps) => {
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const rotatingTexts = ["Bold experiences", "Drag culture", "Queer joy", "Kink spaces", "Trans spaces", "Rebel parties"];
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTextIndex(prevIndex => (prevIndex + 1) % rotatingTexts.length);
    }, 3000); // Change text every 3 seconds

    return () => clearInterval(interval);
  }, []);
  return <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img src="https://cloudflare.slay.events/slay_events_v1_generic_masthead_photo.jpg" alt="Slay Events celebration" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 to-black/60" />
      </div>

      {/* Content Container */}
      <div className="relative z-10 container mx-auto px-4 text-center">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        ease: "easeOut"
      }} className="max-w-4xl mx-auto">
          {/* Main Headline */}
          <div className="space-y-6 mb-8">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white leading-tight">
              We're bringing together
            </h1>
            
            {/* Animated Cycling Text */}
            <div className="relative h-24 md:h-32 lg:h-36 flex items-center justify-center">
              <AnimatePresence mode="wait">
                <motion.h2 key={currentTextIndex} initial={{
                opacity: 0,
                y: 20,
                scale: 0.95
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} exit={{
                opacity: 0,
                y: -20,
                scale: 1.05
              }} transition={{
                duration: 0.6,
                ease: [0.25, 0.1, 0.25, 1]
              }} className="absolute text-4xl md:text-6xl lg:text-7xl font-bold bg-gradient-to-r from-[#9b87f5] via-[#c084fc] to-[#8b5cf6] bg-clip-text text-transparent leading-tight">
                  {rotatingTexts[currentTextIndex]}
                </motion.h2>
              </AnimatePresence>
            </div>
          </div>

          {/* Supporting Text */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.3
        }} className="space-y-4 mb-12">
            <p className="text-xl md:text-2xl lg:text-3xl text-gray-200 font-medium">Every ticket here funds LGBTQ+ creatives and charities.</p>
            <p className="text-xl md:text-2xl lg:text-3xl text-gray-200 font-medium">
              Every moment matters.
            </p>
          </motion.div>

          {/* CTA Button */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.5
        }}>
            <Button onClick={onExploreClick} className="relative bg-[#8b5cf6] hover:bg-[#7c4ef3] text-white px-10 py-6 rounded-full text-xl font-semibold transition-all duration-300 ease-out hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/25 border-2 border-transparent group overflow-hidden">
              <span className="relative z-10">Discover Events</span>
              <div className="absolute inset-0 bg-gradient-to-r from-[#FF0018] via-[#FFA52C] to-[#00B5E2] opacity-0 group-hover:opacity-20 transition-opacity duration-300 rounded-full" />
            </Button>
          </motion.div>
        </motion.div>
      </div>

      {/* Optional: Subtle particles or glow effect */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/3 right-1/3 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl animate-pulse delay-1000" />
      </div>
    </section>;
};